import React, { ReactNode } from "react";
import starIcon from "../../assets/images/star.svg";
import usePageRating from "../../hooks/usePageRating";
import useTranslations from "../../hooks/useTranslations";
import { isNullOrWhiteSpace } from "../../shared/string";
import { getHelpCardColorTheme, HelpCardColorTheme, HelpCardType } from "./HelpCard";
import HelpCardContainer, { Props as ContainerProps } from "./HelpCardContainer";

export type SanityHelpCardWithRating = {
  // Sanity-schema sourced props
  _type: HelpCardType.Rating;
  title: string;
  numberOfStars?: number;
};

export interface Props extends ContainerProps, SanityHelpCardWithRating {
  rightPaddedTitle?: boolean;
  removeButtonXPadding?: boolean;
  buttonTransparent?: boolean;
}

const HelpCardWithRating = (props: Props) => {
  const { className, title, numberOfStars = 5, ...containerProps } = props;

  const translations = useTranslations();
  const [pageRating, updatePageRating] = usePageRating();

  const {
    textColor,
    ratingCircleBackgroundColor,
    ratingCircleTextColor,
    totalReviewsTextColor
  } = getHelpCardColorTheme(containerProps.colorTheme ?? HelpCardColorTheme.Default);

  const repeat = (length: number, item: (index: number) => ReactNode) => {
    const items: ReactNode[] = [];
    for (let i = 0; i < length; ++i) {
      items.push(item(i));
    }

    return items;
  };

  const ratingFloored = pageRating.active ? Math.floor(pageRating.averageRating ?? 0) : 0;

  return (
    <HelpCardContainer
      className={`${className ?? ""} py-10 items-center`}
      roundedClassName="md:rounded-lg"
      outsidePadding={false}
      {...containerProps}
    >
      <div className="flex justify-center mb-4">
        <div
          className={`flex w-24 h-24 justify-center items-center font-bold rounded-full ${ratingCircleBackgroundColor}`}
        >
          <div className={`flex flex-col items-center ${ratingCircleTextColor}`}>
            <span className="text-4.5xl leading-none">
              {pageRating.active && pageRating.averageRating
                ? Math.floor(pageRating.averageRating * 100) / 100
                : "N/A"}
            </span>
            <span className="font-bold uppercase">Rating</span>
          </div>
        </div>
      </div>
      {!isNullOrWhiteSpace(title) && (
        <h3 className={`font-bold ${textColor} text-center my-1 text-2xl md:text-3xl`}>
          {title}
        </h3>
      )}
      {pageRating.active && (
        <ul
          className={`group pointer-events-none flex ${textColor} text-lg font-bold mb-8`}
        >
          {repeat(numberOfStars, index => {
            let dynamicClasses = "";
            let selected = false;
            if (pageRating.userRating && index < pageRating.userRating) {
              selected = index === pageRating.userRating - 1;
              dynamicClasses = "bg-rating-star-highlighted";
            } else {
              dynamicClasses += `group-hover:bg-rating-star-highlighted ${
                index < ratingFloored ? "light" : "dark"
              }`;
            }

            return (
              <li
                key={index}
                className={`
                                    ${dynamicClasses} 
                                    group-tier-2
                                    rating-list-item
                                    relative
                                    transform 
                                    transition-colors 
                                    shadow-soft-sm
                                    duration-200 
                                    pointer-events-auto 
                                    flex 
                                    justify-center 
                                    items-center 
                                    mr-3 
                                    last:mr-0 
                                    rounded 
                                    bg-opacity-2 
                                    cursor-pointer`}
                style={{ width: "59px", height: "59px" }}
                onClick={() => updatePageRating(index + 1)}
              >
                <svg className="w-8 h-8">
                  <use xlinkHref={starIcon.url} />
                </svg>
                <div
                  className={`${
                    selected ? "opacity-100" : "opacity-0"
                  } group-tier-2-hover:opacity-100 pointer-events-none block absolute left-1/2 bottom-0 -mb-8 uppercase transform -translate-x-1/2 transition-opacity duration-200`}
                >
                  {translations[`ratingDescription${index}`]}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {pageRating.active && (
        <span
          className={`flex uppercase ${totalReviewsTextColor} justify-center mt-auto`}
        >
          {pageRating.numRatings ?? 0} total reviews
        </span>
      )}
    </HelpCardContainer>
  );
};

export default HelpCardWithRating;
